import React, { useEffect, useState } from 'react';
import { Theme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import {
  APIActions,
  Button,
  CustomDialog,
  Grid,
  HtmlTrans,
  Icon,
  Link,
  Progress,
  Typography,
} from 'web-library';
import { useTranslation } from 'react-i18next';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { formatBytes } from 'web-library/utils/FileUtils';
import { UploadedFile } from 'web-library/models/UploadedFile';
import { uploadToPresignLink } from '../../pages/accessArrangement/SelectCondition/components/UploadDocuments/components/useUploadFile';
import { useGetDocumentUploadStatus } from '../../api-refactored/document/useGetDocumentUploadStatus';
import { useGetAdminApplicationFileStatus } from '../../api-refactored/manage-announcements/useGetAdminApplicationFileStatus';
import { ApplicationType } from '../../api/candidate/config/model';
import { useGetAdminDownloadApplicationFilePresigned } from '../../api-refactored/manage-announcements/useGetAdminDownloadApplicationFilePresigned';
import { FileUploadState } from '../../models/File';

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) =>
({
  fileBadgeParentContainer: {
    height: theme.spacing(4),
    marginBottom: theme.spacing(4),
    display: "flex",
    flexDirection: "row"
  },
  fileBadgeContainer: {
    height: theme.spacing(4),
    flex: "1"
  },
  removeCell: {
    width: theme.spacing(12),
  },
  fileSize: {
    justifySelf: 'start',
    width: theme.spacing(10),
    whiteSpace: 'nowrap',
    marginRight: theme.spacing(4),
    color: theme.color.text.textTertiary,
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    justifySelf: 'start',
    marginRight: theme.spacing(2),
  },
  documentIcon: {
    color: theme.color.text.textTertiary,
  },
  errorDocumentIcon: {
    color: theme.color.state.stateError,
  },
  fileNameContainer: {
    flexGrow: 2,
  },
  filename: {
    color: theme.color.text.textHeader,
    flexGrow: 2,
  },
  filenameDownloadable: {
    color: theme.color.link.linkDefault01,
    flexGrow: 2,
    fontWeight: 'bold',
  },
  filenameResolved: {
    textOverflow: 'ellipsis',
    fontWeight: 'bold',
    color: theme.color.link.linkDefault01,
    flexGrow: 2,
  },
  removeText: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    color: theme.color.state.stateError,
    fontWeight: 'bold',
  },
  removeButton: {
    padding: 0,
    height: theme.spacing(4),
  },
  removeIcon: {
    padding: 0,
    marginRight: theme.spacing(1),
  },
  errorText: {
    color: theme.color.state.stateError,
    marginTop: theme.spacing(1),
  },
}));

const getIcon = (isLoading: boolean, hasError: boolean, classes: any) => {
  switch (true) {
    case isLoading:
      return <Progress size="M" type="circle" />;
    case hasError:
      return <Icon size="M" name={IconName.alertOff} className={classes.errorDocumentIcon} />;
    default:
      return <Icon size="M" name={IconName.document} className={classes.documentIcon} />;
  }
};

const fileKeyValue = (presignKey?: string) => presignKey || '';

interface IFileBadgeBaseProps {
  isLoading: boolean;
  presignKey?: string;
  onUpdateFileUploadComplete: (
    files: UploadedFile[],
    setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>,
    fileKey: string,
    uploadError: boolean
  ) => void;
}

interface FileBadgeProps extends IFileBadgeBaseProps {
  isOpsAdmin: boolean;
  onUploadFileDelete?: (file: File) => void;
  file: File;
  disableRemoveButton?: boolean;
  hasError: boolean;
  presignedUrl?: string;
  uploadedFilesHandler: {
    files: UploadedFile[];
    setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  };
  downloadableLink?: boolean;
  buttonRemoveIdx?: number;
  hideRemoveButton?: boolean;
  loadingText?: string;
  showLoadingText?: boolean;
}

const FileBadge = ({
  isOpsAdmin,
  onUploadFileDelete,
  file,
  presignKey,
  disableRemoveButton,
  presignedUrl,
  isLoading,
  hasError,
  uploadedFilesHandler,
  onUpdateFileUploadComplete,
  downloadableLink = false,
  buttonRemoveIdx,
  hideRemoveButton,
  loadingText,
  showLoadingText,
}: FileBadgeProps) => {
  const [fileStatusSuccess, setFileStatusSuccess] = useState<boolean>(false);
  const [downloadLink, setDownloadLink] = useState<string>();
  const [deleteModel, setDeleteModel] = useState<boolean>(false);
  const { files, setFiles } = uploadedFilesHandler;
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [timeoutRejectedErrorMessage, setTimeoutRejectedErrorMessage] = useState<string>('');
  const [hasFailureError, setHasFailureError] = useState<string>('');

  useEffect(() => {
    if (presignedUrl && presignKey) {
      setHasFailureError('');
      setTimeoutRejectedErrorMessage('');
      uploadToPresignLink(presignedUrl, presignKey, file);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presignKey, presignedUrl]);
  useGetDocumentUploadStatus({
    fileKey: fileKeyValue(presignKey),
    enabled: !!presignKey && !fileStatusSuccess && !isOpsAdmin,
    refetchInterval: (data) => data?.fileStatus === FileUploadState.UPLOADING ? 3000 : false,
    onSuccess: (data) => {
      if (data.fileStatus === FileUploadState.SUCCESS && presignKey) {
        setFileStatusSuccess(true);
        setDownloadLink(data.url);
        onUpdateFileUploadComplete(files, setFiles, presignKey, false);
      }
      if (data.fileStatus === FileUploadState.FAILED && presignKey) {
        setFileStatusSuccess(true);
        onUpdateFileUploadComplete(files, setFiles, presignKey, true);
        setHasFailureError(t('accessArrangement->>SelectCondition.UploadFailed'));
      }
      if (data.fileStatus === FileUploadState.TIMEOUT && presignKey) {
        setFileStatusSuccess(true);
        onUpdateFileUploadComplete(files, setFiles, presignKey, true);
        setTimeoutRejectedErrorMessage(t('exam->>UploadDocument.UploadingTimeoutMessage'));
      }
    },
  });

  useGetAdminApplicationFileStatus({
    fileUuid: fileKeyValue(presignKey),
    applicationType: ApplicationType.ANNOUNCEMENT,
    enabled: !!presignKey && !fileStatusSuccess && isOpsAdmin,
    refetchInterval: (data) => data?.status === FileUploadState.UPLOADING ? 3000 : false,
    onSuccess: (data) => {
      if (data.status === 'SUCCESS' && presignKey) {
        setFileStatusSuccess(true);
        onUpdateFileUploadComplete(files, setFiles, presignKey, false);
      }
      if (data.status === 'FAILED' && presignKey) {
        setFileStatusSuccess(true);
        onUpdateFileUploadComplete(files, setFiles, presignKey, true);
      }
      if (data.status === 'TIMEOUT' && presignKey) {
        setFileStatusSuccess(true);
        onUpdateFileUploadComplete(files, setFiles, presignKey, true);
        setTimeoutRejectedErrorMessage(t('exam->>UploadDocument.UploadingTimeoutMessage'));
        }
    },
  });

  useGetAdminDownloadApplicationFilePresigned({
    fileUuid: fileKeyValue(presignKey),
    enabled: !!presignKey && isOpsAdmin,
    onSuccess: (data) => {
      if (data.fileStatus === FileUploadState.SUCCESS && presignKey) {
        setDownloadLink(data.url);
      }
    },
  });

  const displayIcon = () => getIcon(isLoading, hasError, classes);

  const showDownloadLink = downloadableLink && downloadLink;
  const loadingTextDisplayed = showLoadingText && loadingText && isLoading ? loadingText : '';
  const downloadableFileNameText = () => (
    <Typography
      noWrap
      className={`${!isLoading && !hasError && !hideRemoveButton
        ? classes.filenameResolved
        : classes.filenameDownloadable
        }`}
    >
      {loadingTextDisplayed}
      {file.name}
    </Typography>
  );

  const fileNameText = () => (
    <Typography
      noWrap
      className={!showLoadingText ? classes.filenameDownloadable : classes.filename}
    >
      {loadingTextDisplayed}
      {file.name}
    </Typography>
  );

  return (
    (<Grid className={classes.fileBadgeParentContainer}>
      <Grid
        container
        md={10}
        xs={12}
        lg={10}
        direction="row"
        alignItems="center"
        justify="flex-start"
        wrap="nowrap"
        className={classes.fileBadgeContainer}
      >
        <Grid className={classes.iconContainer}>{displayIcon()}</Grid>
        {showDownloadLink ? (
          <Link
            target="_blank"
            href={downloadLink}
            style={{ overflow: 'hidden', flexGrow: 2 }}
            rel="noopener noreferrer"
          >
            {downloadableFileNameText()}
          </Link>
        ) : (
          fileNameText()
        )}

        {!isLoading && !hasError && (
          <>
            <Grid container justify="flex-end" className={classes.fileSize}>
              {formatBytes(file.size)}
            </Grid>
            <Grid container className={classes.removeCell}>
              {!hideRemoveButton && (
                <Button
                  id={`button-remove-${buttonRemoveIdx}`}
                  className={`${classes.removeButton} removeButton`}
                  variant="text"
                  onClick={() => setDeleteModel(true)}
                  disabled={disableRemoveButton}
                >
                  <Typography variant="body2" className={classes.removeText}>
                    <Icon
                      size="S"
                      color="error"
                      name={IconName.trash}
                      className={classes.removeIcon}
                    />
                    {t('accessArrangement->>SelectCondition.FileBadgeRemove')}
                  </Typography>
                </Button>
              )}
            </Grid>
          </>
        )}
      </Grid>
      {(hasFailureError || timeoutRejectedErrorMessage) && (
         <Typography className = {classes.errorText}>
           {hasFailureError || timeoutRejectedErrorMessage}
         </Typography>
      )}
      <CustomDialog
        dialogSize="md"
        open={deleteModel}
        title={t('components->>RemoveFile.FileRemoveTitle')}
        message={
          <HtmlTrans
            i18nKey="components->>RemoveFile.FileRemoveConfirmationMsg"
            values={{
              fileName: file.name,
            }}
          />
        }
        action={
          <APIActions
            onClose={() => setDeleteModel(false)}
            apiCall={() => onUploadFileDelete && onUploadFileDelete(file)}
            cancelString={t('components->>RemoveFile.Cancel')}
            apiString={t('components->>RemoveFile.Proceed')}
          />
        }
      />
    </Grid>)
  );
};

export default FileBadge;
